import React, { useState, useCallback } from 'react';
import { Button } from './ui/Button';
import { useToast } from './Toast';

const formatSize = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
};

const FileUpload = ({ onUpload, uploading }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const toast = useToast();

  const validateAndSet = useCallback((file) => {
    // Accept by extension, not just MIME type — browsers/OSes report
    // inconsistent MIME types for .txt files (often empty on drag-and-drop).
    const isTxt = file && (file.name?.toLowerCase().endsWith('.txt') || file.type === 'text/plain');
    if (isTxt) { setSelectedFile(file); return true; }
    toast('Пожалуйста, выберите файл .txt', 'warning');
    return false;
  }, [toast]);

  const handleSelect = useCallback((e) => validateAndSet(e.target.files[0]), [validateAndSet]);
  const handleDrop = useCallback((e) => { e.preventDefault(); setDragOver(false); validateAndSet(e.dataTransfer.files[0]); }, [validateAndSet]);
  const handleDragOver = useCallback((e) => { e.preventDefault(); setDragOver(true); }, []);
  const handleDragLeave = useCallback(() => setDragOver(false), []);
  const handleUpload = useCallback(() => { if (selectedFile) { onUpload(selectedFile); setSelectedFile(null); } }, [selectedFile, onUpload]);
  const handleRemove = useCallback(() => {
    setSelectedFile(null);
    const input = document.getElementById('fileInput');
    if (input) input.value = '';
  }, []);

  return (
    <div className="flex flex-col gap-lg">
      <div
        className={`rounded-lg border-2 border-dashed p-2xl text-center transition-colors duration-200 cursor-pointer
          ${dragOver
            ? 'border-primary bg-primary-soft'
            : 'border-border-strong bg-surface-sunken hover:border-primary/60 hover:bg-muted'}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        role="button"
        tabIndex={0}
        aria-label="Перетащите файл или нажмите для выбора"
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') document.getElementById('fileInput')?.click(); }}
      >
        <input type="file" accept=".txt" onChange={handleSelect} className="hidden" id="fileInput" />
        <label htmlFor="fileInput" className="m-0 flex w-full cursor-pointer flex-col items-center gap-md">
          <i
            className={`bi bi-cloud-arrow-up text-page-title transition-colors duration-200 ${dragOver ? 'text-primary' : 'text-text-muted'}`}
            aria-hidden="true"
          ></i>
          <p className="text-body-md text-text">
            Перетащите файл{' '}
            <code className="rounded-sm bg-muted px-sm py-xs font-mono text-body-sm text-accent">.txt</code>{' '}
            или <span className="font-medium text-primary">выберите файл</span>
          </p>
          <p className="text-body-xs text-text-muted">Формат: одна ссылка на строку</p>
        </label>
      </div>

      {selectedFile && (
        <div className="flex items-center gap-md rounded-md border border-border bg-surface-sunken px-lg py-md animate-slide-down">
          <i className="bi bi-file-earmark-text text-primary" aria-hidden="true"></i>
          <span className="min-w-0 truncate text-body-sm text-text-strong" title={selectedFile.name}>
            {selectedFile.name}
          </span>
          <span className="tabular whitespace-nowrap text-body-xs text-text-muted">
            {formatSize(selectedFile.size)}
          </span>
          <Button
            variant="plain"
            size="icon"
            icon="bi-x-lg"
            title="Убрать файл"
            aria-label="Убрать файл"
            onClick={handleRemove}
            className="ml-auto"
          />
        </div>
      )}

      <div>
        <Button
          variant="primary"
          size="lg"
          icon="bi-upload"
          loading={uploading}
          disabled={!selectedFile || uploading}
          onClick={handleUpload}
          className="w-full justify-center sm:w-auto"
        >
          {uploading ? 'Обработка…' : 'Загрузить и анализировать'}
        </Button>
      </div>
    </div>
  );
};

export default FileUpload;
